import { Either, Left, Right } from 'fp-ts/lib/Either'
import { fromNullable, none, Option } from 'fp-ts/lib/Option'

import { log } from '@/log'
import Variable from '@/models/api/payment/parameter/type/variable'
import { getFullName, getPhoneticFullName } from '@spa/store/modules/user/functions'
import { User } from '@spa/store/modules/user/types'

/**
 * @see https://my-color.esa.io/posts/534
 */
export default interface VariableResolver {
  resolve(variable: Variable): Either<Error, Option<string>>
}

export interface ResolverResource {
  user?: User,
  orderDisplayId?: string,
  externalPaymentId?: string,
  paymentAuthToken?: string,
}

export class DefaultResolver implements VariableResolver {

  constructor(
    private readonly resource: ResolverResource
  ) {}

  resolve(variable: Variable): Either<Error, Option<string>> {
    const value = this.value(variable.variableId)

    if (variable.required && value.isNone()) {
      return new Left(new RequiredValueNotFoundError(`Value of '${variable.variableId}' is not found.`))
    }

    return new Right(value)
  }

  private value(variableId: string): Option<string> {
    switch (variableId) {
      case 'order_display_id':
        return this.orderDisplayId
      case 'external_payment_id':
        return this.externalPaymentId
      case 'payment_auth_token':
        return this.paymentAuthToken
      case 'user_email':
        return this.userEmail
      case 'user_full_name':
        return this.userFullName
      case 'user_phonetic_full_name':
        return this.userPhoneticFullName
      case 'user_tel':
        return this.userTel
      default:
        log.warn(`Unexpected variable id ${variableId}`)

        return none
    }
  }

  private get orderDisplayId(): Option<string> {
    return fromNullable(this.resource.orderDisplayId)
  }

  private get externalPaymentId(): Option<string> {
    return fromNullable(this.resource.externalPaymentId)
  }

  private get paymentAuthToken(): Option<string> {
    return fromNullable(this.resource.paymentAuthToken)
  }

  private get user(): Option<User> {
    return fromNullable(this.resource.user)
  }

  private get userEmail(): Option<string> {
    return this.user.mapNullable(user => user.email)
  }

  private get userFullName(): Option<string> {
    return this.user.map(user => getFullName(user))
  }

  private get userPhoneticFullName(): Option<string> {
    return this.user.map(user => getPhoneticFullName(user))
  }

  private get userTel(): Option<string> {
    return this.user.mapNullable(user => user.tel)
  }
}

export class RequiredValueNotFoundError extends Error {}
